import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Empty,
  Flex,
  Segmented,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import {
  AimOutlined,
  EnvironmentOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSupervisorOperationsMap } from '~/app/hooks/site/useSupervisorOperationsMap';
import {
  clearGoogleMapsInstanceListeners,
  createGoogleMapsMarker,
  isGoogleMapsMissingApiKeyError,
  loadGoogleMapsScript,
  withGoogleMapsMapId,
} from '~/app/lib/google-maps';
import { SUPERVISOR_OPERATIONS_MAP_STYLES } from '~/app/lib/google-maps-presets';
import { SupervisorOperationsMapDetailsPanel } from '~/app/dashboard/components/SupervisorOperationsMapDetailsPanel';
import { SupervisorOperationsMapSummaryTags } from '~/app/dashboard/components/SupervisorOperationsMapSummaryTags';
import type {
  HoveredLocation,
  MapLayer,
  MarkerEntry,
} from '~/app/dashboard/components/SupervisorOperationsMapCard.types';
import {
  buildSupervisorOperationsMapMarkers,
  getActiveHoveredLocation,
  getLocationOpenHref,
  getMapZoom,
  getMissingCoordinatesCount,
  getParentSiteHref,
} from '~/app/dashboard/components/supervisorOperationsMapCard.utils';

const { Text } = Typography;

export function SupervisorOperationsMapCard() {
  const navigate = useNavigate();
  const { data, error, isLoading } = useSupervisorOperationsMap();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<object | null>(null);
  const markerRefs = useRef<object[]>([]);
  const [layer, setLayer] = useState<MapLayer>('all');
  const [hoveredLocation, setHoveredLocation] =
    useState<HoveredLocation | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const markers = useMemo(() => {
    if (!data) {
      return [] as MarkerEntry[];
    }

    return buildSupervisorOperationsMapMarkers(data, layer);
  }, [data, layer]);

  const missingCoordinatesCount = useMemo(() => {
    if (!data) {
      return 0;
    }

    return getMissingCoordinatesCount(data, layer);
  }, [data, layer]);

  const activeHoveredLocation = useMemo(() => {
    return getActiveHoveredLocation(hoveredLocation, markers);
  }, [hoveredLocation, markers]);

  useEffect(() => {
    if (!mapContainerRef.current || markers.length === 0) {
      return;
    }

    const center = {
      lat:
        markers.reduce((sum, marker) => sum + marker.position.lat, 0) /
        markers.length,
      lng:
        markers.reduce((sum, marker) => sum + marker.position.lng, 0) /
        markers.length,
    };
    let isCancelled = false;

    void loadGoogleMapsScript()
      .then((googleMaps) => {
        if (isCancelled || !mapContainerRef.current) {
          return;
        }

        setMapError(null);

        markerRefs.current.forEach((marker) => {
          clearGoogleMapsInstanceListeners(marker);
          (marker as { setMap?: (map: object | null) => void }).setMap?.(null);
        });
        markerRefs.current = [];

        mapRef.current = new googleMaps.maps.Map(
          mapContainerRef.current,
          withGoogleMapsMapId({
            center,
            zoom: getMapZoom(markers),
            disableDefaultUI: false,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
            styles: SUPERVISOR_OPERATIONS_MAP_STYLES,
          }),
        );

        markerRefs.current = markers.map((marker) => {
          const markerInstance = createGoogleMapsMarker(googleMaps, {
            map: mapRef.current as object,
            position: marker.position,
            title: marker.title,
            icon: marker.icon,
            zIndex: marker.zIndex,
          });

          markerInstance.addListener?.('mouseover', () => {
            setHoveredLocation(marker.payload);
          });
          markerInstance.addListener?.('click', () => {
            navigate(marker.href);
          });

          return markerInstance;
        });
      })
      .catch((caughtError: Error) => {
        if (isCancelled) {
          return;
        }

        setMapError(
          isGoogleMapsMissingApiKeyError(caughtError)
            ? 'Google Maps API key is missing.'
            : caughtError.message,
        );
      });

    return () => {
      isCancelled = true;
      markerRefs.current.forEach((marker) => {
        clearGoogleMapsInstanceListeners(marker);
        (marker as { setMap?: (map: object | null) => void }).setMap?.(null);
      });
      clearGoogleMapsInstanceListeners(mapRef.current);
      markerRefs.current = [];
    };
  }, [markers, navigate]);

  return (
    <Card
      title={<span className="ds-section-header">Operations Map</span>}
      extra={
        <Segmented<MapLayer>
          size="small"
          value={layer}
          onChange={(value) => setLayer(value)}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Sites', value: 'sites' },
            { label: 'Zones', value: 'zones' },
          ]}
        />
      }
      style={{
        width: '100%',
        borderRadius: 18,
        border: '1px solid #d9e1ec',
        overflow: 'hidden',
      }}
      styles={{ body: { padding: 18 } }}
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : error ? (
        <Empty
          description={error.message || 'Unable to load operations map data'}
        />
      ) : !data || markers.length === 0 ? (
        <Empty description="No mapped sites or zones are available yet" />
      ) : (
        <Flex vertical gap={16}>
          <SupervisorOperationsMapSummaryTags
            summary={data.summary}
            missingCoordinatesCount={missingCoordinatesCount}
          />

          <div style={{ position: 'relative' }}>
            <div
              ref={mapContainerRef}
              style={{
                minHeight: 460,
                borderRadius: 16,
                overflow: 'hidden',
                background:
                  'radial-gradient(circle at top, rgba(228, 239, 248, 0.9), rgba(242, 246, 250, 1))',
              }}
            />

            {mapError ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 20,
                  textAlign: 'center',
                  background: 'rgba(255,255,255,0.88)',
                }}
              >
                <Text type="secondary">{mapError}</Text>
              </div>
            ) : null}

            {activeHoveredLocation ? (
              <SupervisorOperationsMapDetailsPanel
                activeHoveredLocation={activeHoveredLocation}
                onOpenLocationAction={(location) =>
                  navigate(getLocationOpenHref(location))
                }
                onOpenParentSiteAction={(siteId) =>
                  navigate(getParentSiteHref(siteId))
                }
              />
            ) : null}
          </div>

          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <Space size={18} wrap>
              <Space size={6}>
                <AimOutlined style={{ color: '#2563eb' }} />
                <Text type="secondary">Ring markers are sites</Text>
              </Space>
              <Space size={6}>
                <EnvironmentOutlined style={{ color: '#16a34a' }} />
                <Text type="secondary">Pins are zones</Text>
              </Space>
              <Space size={6}>
                <WarningOutlined style={{ color: '#dc2626' }} />
                <Text type="secondary">
                  Hover a marker to inspect and click to navigate
                </Text>
              </Space>
            </Space>
            <Text type="secondary">
              Colors reflect the most severe asset state at each location.
            </Text>
          </Flex>
        </Flex>
      )}
    </Card>
  );
}
